import styled from 'styled-components'
import { motion } from 'framer-motion'

const DEFAULT_ASPECT_RATIO = '2.7 / 3'
const COMPACT_ASPECT_RATIO = '1 / 1'

export const Card = styled(motion.article)`
  position: relative;
  width: 100%;
  max-width: ${({ $size }) => ($size === 'sm' ? '100px' : '200px')};
  aspect-ratio: ${({ $compactAspect }) =>
    $compactAspect ? COMPACT_ASPECT_RATIO : DEFAULT_ASPECT_RATIO};
  border-radius: ${({ $size }) => ($size === 'sm' ? '8px' : '14px')};
  overflow: hidden;
  border: ${({ $size }) => ($size === 'sm' ? '1px' : '2px')} solid #1e1e1e;
  background-color: #111;
  transform-origin: center center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;
  cursor: pointer;
`

export const ShineContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
`

export const ShineSweep = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 30%;
  height: 150%;
  background: rgba(255, 255, 255, 0.2);
  transform: skewX(-25deg);
  box-shadow: 0 0 20px 10px rgba(255, 255, 255, 0.2);
`

export const BlurredBackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(10px);
  opacity: 0.7;
  transform: scale(1.04);
  display: block;
  pointer-events: none;
`

export const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: ${({ $compactAspect }) => ($compactAspect ? 'cover' : 'contain')};
  display: block;
`

export const BottomGradient = styled.div`
  position: absolute;
  inset: auto 0 0;
  height: 55%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0.72) 100%
  );
  pointer-events: none;
`

export const TopLeftBadges = styled.div`
  position: absolute;
  top: ${({ $size }) => ($size === 'sm' ? '4px' : '8px')};
  left: ${({ $size }) => ($size === 'sm' ? '4px' : '8px')};
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: ${({ $size }) => ($size === 'sm' ? '4px' : '6px')};
`

export const ExclusiveBadge = styled.span`
  position: relative;
  z-index: 1;
  padding: ${({ $size }) => ($size === 'sm' ? '0.1rem 0.25rem' : '0.2rem 0.45rem')};
  border-radius: ${({ $size }) => ($size === 'sm' ? '3px' : '4px')};
  background: #ea0b3f;
  background: #8dea0b3d;
  border: 1px solid #82ea0b;
  color: #fff;
  font-family: 'Chivo', sans-serif;
  font-size: ${({ $size }) => ($size === 'sm' ? '0.55rem' : '0.95rem')};
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
  text-shadow: 1px 1px 2px #000000;
`

export const HotBadge = styled.div`
  position: relative;
  z-index: 1;
  background-color: #000000b5;
  width: ${({ $size }) => ($size === 'sm' ? '1.5rem' : '1.7rem')};
  height: ${({ $size }) => ($size === 'sm' ? '1.5rem' : '1.7rem')};
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: orangered;
    font-size: ${({ $size }) => ($size === 'sm' ? '1.2rem' : '1.4rem')};
  }
`

export const HoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: brightness(1.18) saturate(1.6);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`

export const ActionButtons = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.6) 35%,
    rgba(0, 0, 0, 0.82) 100%
  );
`

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: ${({ $fullWidth }) => ($fullWidth ? '1 1 100%' : '1 1 50%')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '50%')};
  max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '50%')};
  height: ${({ $size, $compactAspect, $fullWidth }) => {
    if ($fullWidth && !$compactAspect) {
      return $size === 'sm' ? '3.5rem' : '4.25rem'
    }

    return $size === 'sm' ? '2.7rem' : '3.25rem'
  }};
  padding: 0;
  border: none;
  border-top: 1.5px solid rgba(255, 255, 255, 0.85);
  border-radius: 0;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  font-family: 'Chivo', sans-serif;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  font-size: ${({ $size, $compactAspect, $fullWidth }) =>
    $fullWidth && !$compactAspect
      ? ($size === 'sm' ? '0.82rem' : '1rem')
      : ($size === 'sm' ? '0.72rem' : '0.92rem')};
  line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease;

  min-width: 0;

  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.35);
  }

  i {
    font-size: ${({ $size, $compactAspect, $fullWidth }) =>
      $fullWidth && !$compactAspect
        ? ($size === 'sm' ? '0.95rem' : '1.15rem')
        : ($size === 'sm' ? '0.85rem' : '1.05rem')};
    margin-left: 1px;
    line-height: 1;
  }

  &:hover {
    background: rgba(0, 0, 0, 1);
    border-top-color: #78bc28;
    color: #78bc28;
  }

  &:focus-visible {
    outline: 2px solid #78bc28;
    outline-offset: -2px;
  }
`

export const InfoWrapper = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 5;

  .slots-game-info-link {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    color: #fff;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.85));
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.15);
    }
  }
`

export const RankNumber = styled(motion.span)`
  position: absolute;
  left: 50%;
  bottom: ${({ $size }) => ($size === 'sm' ? '0.15rem' : '0.35rem')};
  z-index: 2;
  font-family: 'Chivo', sans-serif;
  font-size: ${({ $size }) =>
    $size === 'sm' ? 'clamp(2.5rem, 42%, 3rem)' : 'clamp(9.5rem, 42%, 5.5rem)'};
  font-weight: 900;
  line-height: 1;
  color: #fff;
  letter-spacing: -0.04em;
  pointer-events: none;
  user-select: none;
  text-shadow:
    1px 1px 0 #d8d8d8,
    2px 2px 0 #404040,
    3px 3px 0 #5e5d5d,
    4px 4px 0 #1d3d00,
    5px 5px 0 #266d00,
    6px 6px 12px rgba(0, 0, 0, 0.45);
`
